import { createAsyncThunk } from "@reduxjs/toolkit";
import { DeleteParams, FetchParams, PostParams, PutParams, RejectErrorValue, requestOK } from "redux-thunk-kit";
import { AppError } from "ry-error-wrapper";
import { ENDPOINTS } from "~/constants";
import { EUploadFileStatus } from "~/constants/dashboard";
import { VerifyResult, VerifySingleResult } from "~/redux/dashboards/slice";
import { RootState } from "~/redux/root-reducer";
import { AppDispatch } from "~/redux/root-store";
import {
  ConfirmPaymentApiItem,
  HeadshotStyleApiItem,
  ImageAcceptedFile,
  JobApiItem,
  JobPendingApiItem,
  OutfitApiItem,
  PackageApiItem,
  PackageKey,
  PaymentType,
  SurveyApiItem,
  VerifyResultImage,
} from "~/types/models/dashboard";
import { getFileKey } from "~/utils/helpers/dashboard-dropzone";
import request from "~/utils/request";
import { thunk } from "~/utils/thunk";
import { Quota, Subscription } from "~/types/models/user";

export interface FetchDashboardsParams extends FetchParams {
  orderBy?: string;
}

export interface FetchQuotaParams extends FetchParams {}

export interface FetchSubscriptionParams extends FetchParams {}

export interface AddDashboardParams extends PostParams {
  data: {};
}

export interface CreatePaymentSession extends PostParams {
  data: {
    cancel_url: string;
    success_url: string;
    id?: number; // temporary
    package_key: Omit<PackageKey, "team-plan">;
    job_id?: string;
    type: Omit<PaymentType, "job">;
  };
}

export interface ConfirmPaymentParams extends PostParams {
  data: {
    sessionId: string;
    jobId?: string;
    type: Omit<PaymentType, "job">;
  };
}

export interface CreateBillPortalSessionParams extends PostParams {
  data: {
    success_url: string;
  };
}

export interface ToggleFavorite extends PostParams {
  data: {
    is_favorited: boolean;
  };
  imageID: string;
}

export interface ToggleFavoriteAll extends PostParams {
  data: {
    is_favorited: boolean;
  };
  jobID: string;
}

export interface ToggleVoting extends PostParams {
  data: {
    like: boolean;
  };
  imageID: string;
}

interface KeyFeatureParam {
  age: number;
  ethnicity: string;
  eye_color: string;
  name: string;
  sex: string;
}
export interface ConfirmJobParams extends PostParams {
  data: {
    key_features: KeyFeatureParam[];
    headshot_styles: HeadshotStyleApiItem[];
    styles: {
      background: string;
      clothing: string;
    }[];
    survey_ids: string[];
  };
  id: string;
}

export interface ConfirmJobInSubscriptionParams extends PostParams {}

export interface ConfirmUpsellJobParams extends PostParams {
  data: {
    headshot_styles: HeadshotStyleApiItem[];
  };
  id: string;
}

export interface JobItemParams extends FetchParams {
  id: string;
}

export interface SharedJobItemParams extends FetchParams {
  slug: string;
}

export interface UpdateDashboardParams extends PutParams {
  data: {};
  id: string;
}

export interface RemoveSelectedPhoto extends DeleteParams {
  imageId: string;
}

export interface ReturnTypeUploadPhotoMisc {
  invalidImage: VerifyResultImage;
  validImage: VerifyResultImage;
  fileId: string;
  status: EUploadFileStatus;
  errMsg?: string;
}

const query = thunk.query("dashboard");
export const fetchPackages = query.fetch<FetchDashboardsParams, PackageApiItem[]>(ENDPOINTS.PACKAGE, "fetchPackages", {
  withoutNormalize: true,
});

export const fetchOutfits = query.fetch<FetchDashboardsParams, OutfitApiItem[]>(ENDPOINTS.OUTFIT, "fetchOutfits", {
  withoutNormalize: true,
});

export const fetchSurveys = query.fetch<FetchDashboardsParams, SurveyApiItem[]>(ENDPOINTS.SURVEY, "listSurveys", {
  withoutNormalize: true,
});

export const createPaymentSession = query.post<CreatePaymentSession, { checkout_url: string }>(
  ENDPOINTS.PAYMENT_SESSION,
  "createPaymentSession",
  {
    withoutNormalize: true,
  }
);

export const confirmPayment = query.post<ConfirmPaymentParams, ConfirmPaymentApiItem>(
  ENDPOINTS.PAYMENT_CONFIRM,
  "confirmPayment",
  {
    withoutNormalize: true,
  }
);

export const createBillPortalSession = query.post<CreateBillPortalSessionParams, any>(
  ENDPOINTS.BILLING_PORTAL,
  "billingPortal",
  {
    withoutNormalize: true,
  }
);

export const toggleFavorite = query.post<ToggleFavorite>(ENDPOINTS.FAVORITE, "favorite", {
  withoutNormalize: true,
});

export const toggleFavoriteAll = query.post<ToggleFavoriteAll>(ENDPOINTS.FAVORITE_ALL, "favoriteAll", {
  withoutNormalize: true,
});

export const toggleVoting = query.post<ToggleVoting>(ENDPOINTS.VOTE, "vote", {
  withoutNormalize: true,
});

export const removeSelectedPhoto = createAsyncThunk<
  RemoveSelectedPhoto,
  { imageId: string },
  { dispatch: AppDispatch; rejectValue: RejectErrorValue; state: RootState }
>("removeSelectedPhoto", async ({ imageId }, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    if (state.dashboard.jobId) {
      const res = await request.delete(`/app/jobs/${state.dashboard.jobId}/photos/${imageId}`);
      return { ...res?.data?.data, message: res?.data?.message, imageId };
    }
    throw new AppError("Invalid JobId");
  } catch (e) {
    new AppError(e);
    return rejectWithValue({ errMsg: "Can not delete file!" } as RejectErrorValue);
  }
});

export const uploadSinglePhoto = createAsyncThunk<
  VerifySingleResult,
  {
    file: File & { width?: number; height?: number };
    skipShowMessage?: boolean;
    type: "first_person" | "second_person";
  },
  { dispatch: AppDispatch; rejectValue: RejectErrorValue; state: RootState }
>("uploadSinglePhoto", async ({ file, type }, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    if (state.dashboard.jobId) {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("image", file);
      const meta = {
        w: file.width,
        h: file.height,
        size: file.size,
      };
      formData.append("meta", JSON.stringify(meta));
      const res = await request.post(`/app/jobs/${state.dashboard.jobId}/photos`, formData, {
        // headers: { "Content-Type": file.type, "X-Amz-Acl": "public-read" },
        timeout: 300000,
      });
      return { ...res?.data?.data, message: res?.data?.message };
    }
    throw new AppError("Invalid JobId");
  } catch (e) {
    const appError = new AppError(e);
    return rejectWithValue({ errMsg: appError.message } as RejectErrorValue);
  }
});

export const uploadPhotoMisc = query.wrapper<
  { images: ImageAcceptedFile[]; type: "first_person" | "second_person" },
  ReturnTypeUploadPhotoMisc[]
>("uploadPhotoMisc", async (params, { dispatch, getState, rejectWithValue }) => {
  const { images, type } = params;
  const state: RootState = getState();
  const { verifyResult, dashboardProgress } = state.dashboard;
  const invalidImages: VerifyResult["invalid_images"] = verifyResult?.invalid_images || [];
  const validImages: VerifyResult["valid_images"] = verifyResult?.valid_images || [];
  const listValidImgsFileKey = validImages.map(img => getFileKey(img.meta));
  const listInvalidImgsFileKey = invalidImages.map(img => getFileKey(img.meta));
  try {
    const result = await Promise.all(
      images.map(async f => {
        const fileKey = getFileKey(f.file);
        // This code block to check duplicate images between 2 section in UploadPhoto screen
        if (listInvalidImgsFileKey.includes(fileKey) || listValidImgsFileKey.includes(fileKey)) {
          return {
            invalidImage: null,
            validImage: null,
            fileId: fileKey,
            status: EUploadFileStatus.DUPLICATE,
          };
        }
        const resAction = await dispatch(
          uploadSinglePhoto({
            file: f.file,
            skipShowMessage: true,
            type,
          })
        );
        if (requestOK(resAction, uploadSinglePhoto)) {
          // const resMessage = resAction?.payload.message;
          // const isAtVerifyStep =
          //   dashboardProgress.step === EDashboardStep.Upload && dashboardProgress.screenIndex === 4;
          // if (resMessage && isAtVerifyStep) {
          //   dispatch(notify({ message: resMessage, type: "error" }));
          // }

          return {
            invalidImage: {
              ...resAction?.payload.invalid_image,
              localUrl: f.localUrl,
              localMeta: {
                width: f.file?.width,
                height: f.file?.height,
                size: f.file?.size,
              },
              errMsg: resAction?.payload.message,
            },
            validImage: {
              ...resAction?.payload.valid_image,
              localUrl: f.localUrl,
              localMeta: {
                width: f.file?.width,
                height: f.file?.height,
                size: f.file?.size,
              },
            },
            fileId: fileKey,
            status: EUploadFileStatus.SUCCESS,
          };
        }
        return {
          invalidImage: null,
          validImage: null,
          fileId: fileKey,
          status: EUploadFileStatus.FAIL,
          errMsg: resAction.payload?.errMsg,
        };
      })
    );

    const resultDuplicateStatus = result.filter(res => res.status === EUploadFileStatus.DUPLICATE);
    // if (resultDuplicateStatus.length) {
    // dispatch(notify({ message: DUPLICATE_UPLOAD_MSG, type: "error", title: DUPLICATE_UPLOAD_TITLE }));
    // }
    return result;
  } catch (error) {
    rejectWithValue({} as RejectErrorValue);
    throw new AppError(error);
  }
});

export const photoVerify = createAsyncThunk<
  VerifyResult,
  { files: File[] },
  { dispatch: AppDispatch; rejectValue: RejectErrorValue; state: RootState }
>("photoVerify", async ({ files }, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    if (state.dashboard.jobId) {
      const formData = new FormData();
      files.forEach(file => formData.append("images", file));
      const res = await request.post(`/jobs/${state.dashboard.jobId}/verify_photo`, formData);
      return { ...res?.data?.data, message: res?.data?.message };
    }
    throw new AppError("Invalid JobId");
  } catch (e) {
    new AppError(e);
    return rejectWithValue({ errMsg: "Can not upload file!" } as RejectErrorValue);
  }
});

export const confirmJob = query.post<ConfirmJobParams, JobApiItem>(`/app/jobs/:id/confirm`, "confirmJob", {
  withoutNormalize: true,
});

export const createNewAllAccessJob = query.post<any, JobApiItem>(`/app/jobs/all_access`, "newAllAccessJob", {
  withoutNormalize: true,
});

export const fetchMyListJob = query.fetch<FetchDashboardsParams, JobApiItem[]>(ENDPOINTS.MY_JOB, "fetchMyListJob", {
  withoutNormalize: true,
});

export const fetchJobData = query.fetch<JobItemParams, JobApiItem>(`${ENDPOINTS.MY_JOB}/:id`, "fetchJobData", {
  withoutNormalize: true,
});

export const fetchSharedJob = query.fetch<SharedJobItemParams, JobApiItem>(
  `${ENDPOINTS.SHARED_JOB}`,
  "fetchSharedJob",
  {
    withoutNormalize: true,
  }
);

export const fetchPendingJob = query.fetch<FetchDashboardsParams, JobPendingApiItem>(
  `${ENDPOINTS.JOB}/pending`,
  "fetchPendingJob",
  {
    withoutNormalize: true,
  }
);

// export const updateDashboard = query.put<UpdateDashboardParams, DashboardApiItem>(
//   `${ENDPOINTS.DASHBOARDS}/:id`,
//   '',
//   {
//     withoutNormalize: true,
//   },
// );
// export const deleteDashboard = query.delete<DeleteParams, DashboardApiItem>(`${ENDPOINTS.DASHBOARDS}/:id`, '', {
//   withoutNormalize: true,
// });

export const confirmUpsellJob = query.post<ConfirmUpsellJobParams, JobApiItem>(
  `/app/jobs/:id/upsell_headshot_confirm`,
  "confirmUpsellJob",
  {
    withoutNormalize: true,
  }
);

export const fetchQuota = query.fetchOne<FetchQuotaParams, Quota>(`${ENDPOINTS.QUOTA}`, "fetchQuota", {
  withoutNormalize: true,
});

export const fetchSubscriptions = query.fetch<FetchSubscriptionParams, Subscription[]>(
  `${ENDPOINTS.SUBSCRIPTIONS}`,
  "fetchSubscription",
  {
    withoutNormalize: true,
  }
);
